import type { AvailableLegalCountryCode } from './countries';

export const ZENDESK_COUNTRIES = ['fr', 'de', 'es', 'it', 'pt', 'nl', 'be'] as const;
export type ZendeskCountryCode = (typeof ZENDESK_COUNTRIES)[number];

export const ZENDESK_COUNTRY_TO_HELP_CENTER_MAP: Record<
  AvailableLegalCountryCode,
  ZendeskCountryCode
> = {
  FR: 'fr',
  DE: 'de',
  IT: 'it',
  ES: 'es',
  PT: 'pt',
  BE: 'be',
  NL: 'nl',
  // Austria fallbacks to the articles in the German help center
  AT: 'de',
};

export const ZENDESK_DEFAULTS = {
  UNAUTH_FALLBACK_COUNTRY: 'fr',
  UNAUTH_FALLBACK_LOCALE: 'en',
  AUTH_FALLBACK_LOCALE: 'en',
} as const;

const ZENDESK_ALLOWED_COUNTRIES = ['fr', 'de', 'es', 'it', 'pt', 'nl', 'be'] as const;
export type AllowedCountry = (typeof ZENDESK_ALLOWED_COUNTRIES)[number];

export const ZENDESK_BASE_URL = {
  fr: 'https://qonto9015.zendesk.com',
  it: 'https://qonto9015-it.zendesk.com',
  de: 'https://qonto9015-de.zendesk.com',
  es: 'https://qonto9015-es.zendesk.com',
  pt: 'https://qonto9015-pt.zendesk.com',
  nl: 'https://qonto9015-nl.zendesk.com',
  be: 'https://qonto9015-be.zendesk.com',
} as const;

export const ZENDESK_KEYS = {
  fr: 'f5d5db0d-c83c-41b5-ba20-ab8e9e267ae4',
  es: '9b02de52-dc0a-455d-b821-fa4e88afc8d2',
  it: '497b917f-d259-4f04-b309-6c2e8d73f82f',
  de: 'f4bc3933-f306-4248-ad3b-0461d92854b1',
  __sandbox__: '2afdfea0-17eb-44c0-af49-6deeb3fba54b',
};

export const ZENDESK_CATEGORIES_IDS = {
  company_creation: { fr: undefined, de: undefined, it: 23670883303441, es: undefined },
  capital_increase_help: {
    fr: 37463094023185,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
};

export type ZendeskArticlesIds = {
  [slug: string]: {
    [countryCode in ZendeskCountryCode]: number | undefined;
  };
};

export const ZENDESK_ARTICLES_IDS: ZendeskArticlesIds = {
  fx_out_educate_user: {
    fr: 34409633405585,
    de: 34462937686161,
    it: 34462894080145,
    es: 34462812698641,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  ri_ba: {
    fr: undefined,
    de: undefined,
    it: 32583787201937,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  what_are_different_roles_available: {
    fr: 23947722927249,
    de: 23949255133457,
    it: 23949973088145,
    es: 23951013010065,
    pt: 31003715679761,
    nl: 31007461385361,
    be: 32445001087377,
  },
  what_are_multiple_accounts: {
    fr: 23947679356817,
    de: 23949283320977,
    it: 23949957454353,
    es: 23950983707409,
    pt: 31024808687249,
    nl: 31066665899409,
    be: 32617443919761,
  },
  probative_value_certification: {
    fr: 23947663385361,
    de: 23949268610193,
    it: 23949944087185,
    es: 23951002573457,
    pt: 31975240143377,
    nl: 35293677921425,
    be: 32606503497745,
  },
  how_to_receive_swift_transfers: {
    fr: 23947692631825,
    de: 23949284744593,
    it: 23949997871761,
    es: 23951029272081,
    pt: 30796162688145,
    nl: 30821623313169,
    be: 32776215808273,
  },
  cant_create_multiple_accounts: {
    fr: 23947681308177,
    de: 23949232332305,
    it: 23949971182097,
    es: 23950968161809,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  import_multiple_beneficiaries_for_bulk_transfer: {
    fr: 23947717105169,
    de: 23949238255761,
    it: 23949989824529,
    es: 23951024072081,
    pt: 30991164184849,
    nl: 31004745264657,
    be: 32795521353361,
  },
  how_to_pay_with_physical_card_before_receiving_it: {
    fr: 23947627043729,
    de: 23949233278993,
    it: 23949962713489,
    es: 23950925341201,
    pt: 31064494930321,
    nl: undefined,
    be: undefined,
  },
  how_to_pair_my_phone: {
    fr: 23947682067985,
    de: 23949222979985,
    it: 23949957227025,
    es: 23950992516113,
    pt: 31031400901649,
    nl: 31143790593297,
    be: 32483168441745,
  },
  request_higher_monthly_limit_online_ad_card: {
    fr: 23947595341841,
    de: 23949213139729,
    it: 23949895170065,
    es: 23950938696849,
    pt: 31066417259153,
    nl: undefined,
    be: undefined,
  },
  online_advertising_cards: {
    fr: 23947604815633,
    de: 23949227392785,
    it: 23949949195409,
    es: 23950932465169,
    pt: 31066086093329,
    nl: undefined,
    be: 32729898176273,
  },
  how_to_use_analytics_labels: {
    fr: 23947715457297,
    de: 23949270560273,
    it: 23949973995921,
    es: 23951001405457,
    pt: 30991574129169,
    nl: 31005117198865,
    be: 32548660448017,
  },
  charge_back_learn_more: {
    fr: 24513422128529,
    de: 25222939229329,
    it: 25093583613969,
    es: 24243901021201,
    pt: 38924127851409,
    nl: 31351659034129,
    be: undefined,
  },
  charge_back_fraud: {
    fr: 24512685199889,
    de: 27700735752593,
    it: 29454782279313,
    es: 24243782436369,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  what_is_financing_section: {
    fr: 23947581127441,
    de: 23949227806993,
    it: 23949946207505,
    es: 23950868319889,
    pt: 35238702541841,
    nl: 35294698964625,
    be: undefined,
  },
  different_offers_in_financing: {
    fr: 23947605128849,
    de: 23949197790609,
    it: 23949934952977,
    es: 23950953154065,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_use_pay_later: {
    fr: 23947598547985,
    de: 24275960254609,
    it: 23949931720081,
    es: 23950882390417,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  qonto_pay_later_how_it_works: {
    fr: 24475495156881,
    de: 23949210437009,
    it: 23949918640529,
    es: 23950919826961,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  sole_propietor_and_freelancers: {
    fr: undefined,
    de: 23949214964497,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  transfer_methods_for_international_transfer: {
    fr: 24835439794833,
    de: 24835877608337,
    it: 24835842852625,
    es: 24835882281873,
    pt: 31078252352913,
    nl: 31665522780561,
    be: 32773908817297,
  },
  documents_accepted_by_qonto: {
    fr: 23947696072721,
    de: 23949292113937,
    it: 23949991315473,
    es: 23951040015633,
    pt: 30431665979409,
    nl: 30399850923281,
    be: 32214120057233,
  },
  how_sdd_work: {
    fr: 23947694535569,
    de: 23949259949073,
    it: 23949960610961,
    es: 23951030848657,
    pt: 30928922165009,
    nl: 31000948641937,
    be: 32810961616401,
  },
  who_can_oepn_qonto_account_on_my_behalf: {
    fr: 23947688152977,
    de: 23949272305425,
    it: 23949961087889,
    es: 23951031503121,
    pt: 30723405249553,
    nl: 35293429176977,
    be: 32390828581521,
  },
  top_up_account_by_card: {
    fr: 23947665588881,
    de: 23949267077265,
    it: 23949941350289,
    es: 23950988068625,
    pt: 31044119038865,
    nl: 31358186293777,
    be: 32680693142033,
  },
  how_qonto_verifies_integrations_and_partnerships_section: {
    fr: 23947683075857,
    de: 23949242110865,
    it: 23949969471505,
    es: 23951003546257,
    pt: 31004159040017,
    nl: 31066068608785,
    be: 32570773681041,
  },
  how_to_create_a_credit_note: {
    fr: 23947630401681,
    de: 23949214351889,
    it: 23949950234385,
    es: 23950909742097,
    pt: undefined,
    nl: undefined,
    be: 32541162283793,
  },
  how_to_create_customer_quote: {
    fr: 23947623005201,
    de: 23949200354961,
    it: 23949933565457,
    es: 23950928816529,
    pt: undefined,
    nl: undefined,
    be: 32542442857745,
  },
  additional_information_in_customer_invoice: {
    fr: 23947608620305,
    de: 23949195799569,
    it: 23949907221137,
    es: 23950898831121,
    pt: undefined,
    nl: undefined,
    be: 32541524635409,
  },
  what_additional_information_should_my_customer_quote_include: {
    fr: 23947603083537,
    de: 23949216285585,
    it: 23949894422417,
    es: 23950930431633,
    pt: undefined,
    nl: undefined,
    be: 32540948788241,
  },
  referral_program: {
    fr: 23947726568977,
    de: 23949286745745,
    it: 23949975789329,
    es: 23951008451985,
    pt: 30720385927441,
    nl: 36324175802513,
    be: 32800093371537,
  },
  mileage_reimbursement_amount: {
    fr: 23947584856209,
    de: 23949196873233,
    it: 23949948751761,
    es: 23950901011089,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  mirror_card_venice_festival_lottery: {
    fr: 45164339531153,
    de: 45164540398737,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_get_reimbursement_for_an_expense: {
    fr: 23947604237713,
    de: 23949201770897,
    it: 23949908281489,
    es: 23950938065681,
    pt: 31061492304657,
    nl: 31379036953873,
    be: 32608019115025,
  },
  direct_debit_collection_eligibility: {
    fr: 24850425156625,
    de: 26982531979793,
    it: 25276765652113,
    es: 25193162878609,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  generic_closed_account: {
    fr: 23947579160977,
    de: 23949208800145,
    it: 23950013048465,
    es: 23950882263825,
    pt: 31074152414481,
    nl: 31560101187473,
    be: 32925290189329,
  },
  fraud_closed_account: {
    fr: 23947603718801,
    de: 29063380114449,
    it: 23949908091921,
    es: 23950923349009,
    pt: 31070897381905,
    nl: 31381497603089,
    be: 32925290189329,
  },
  how_qonto_fees_are_calculated: {
    fr: 23947674128913,
    de: 23949221022097,
    it: 23949941256593,
    es: 23950947692689,
    pt: 31060048062609,
    nl: 31732136836881,
    be: 32646403428497,
  },
  what_is_regulatory_check_about: {
    fr: 23947740275601,
    de: 23949292824977,
    it: 23949976546833,
    es: 23951032940945,
    pt: 30722900033681,
    nl: 30790317852689,
    be: 32795772237841,
  },
  can_qonto_account_be_overdrawn: {
    fr: 23947715184273,
    de: 23949258372113,
    it: 23950013576081,
    es: 23951005749393,
    pt: 30992902035345,
    nl: 31006844871441,
    be: 32825300251153,
  },
  how_to_use_custom_exports: {
    fr: 23947593685009,
    de: 23949211817873,
    it: 23949948387857,
    es: 23950922077457,
    pt: 31071700393745,
    nl: 31558431851281,
    be: 32554480243089,
  },
  processing_card_transaction: {
    fr: 23947701967505,
    de: 23949275420945,
    it: 23949999378705,
    es: 23951007519249,
    pt: 30928333499921,
    nl: 30996133195281,
    be: 32810850134417,
  },
  release_deposit: {
    fr: 23947668361361,
    de: 23949231950481,
    it: 23949953933969,
    es: 23950995738641,
    pt: 31040558227857,
    nl: 31351124362769,
    be: 32867510776081,
  },
  dispute_a_card_debit: {
    fr: 23947646001425,
    de: 23949231761937,
    it: 23949967365137,
    es: 23950949938705,
    pt: 31041477532561,
    nl: 31351659034129,
    be: 32868140617617,
  },
  facing_commercial_disput: {
    fr: 23947651978897,
    de: 23949237547409,
    it: 23949942577425,
    es: 23950995396881,
    pt: 31040916548497,
    nl: 31351256537745,
    be: 32767333919761,
  },
  required_documents_to_execute_transfer: {
    fr: 23947711177489,
    de: 23949276303505,
    it: 23949990938513,
    es: 23951017243409,
    pt: 30833073158673,
    nl: 30995436860177,
    be: 32749073799825,
  },
  mark_payee_as_trustworthy: {
    fr: 23947644174993,
    de: 23949206250641,
    it: 23949966202129,
    es: 23950947963793,
    pt: 31043095157393,
    nl: 31352340268561,
    be: 32581206496657,
  },
  how_to_contact_support: {
    fr: 31746311823633,
    de: 23949246697873,
    it: 23950004508305,
    es: 23951055377041,
    pt: 30723942545553,
    nl: 36331461186065,
    be: 32420248709521,
  },
  difference_between_swift_and_sepa: {
    fr: 23947689026321,
    de: 23949292338833,
    it: 23949961580945,
    es: 23951040439825,
    pt: 30796304056081,
    nl: 30821032508305,
    be: 32775811817105,
  },
  how_to_make_transfer_to_my_qonto_account: {
    fr: 23947739917969,
    de: 23949246923409,
    it: 23949991779729,
    es: 23951040733073,
    pt: 30723789560209,
    nl: 30820349911825,
    be: 32801727315985,
  },
  transfer_documents_types_for_international_transfer: {
    fr: 23947612047633,
    de: 23949228051089,
    it: 23949935555089,
    es: 23950925626513,
    pt: 31062504690321,
    nl: 31379460075537,
    be: 32774983885713,
  },
  when_to_submit_swift_transfers: {
    fr: 26049468134417,
    de: 23949245587217,
    it: 23950015256593,
    es: 23951024499217,
    pt: 30927948167825,
    nl: 30995944023185,
    be: 32810623413393,
  },
  why_cannot_execute_swift_transfer: {
    fr: 23947694827537,
    de: 23949291324945,
    it: 23949960847377,
    es: 23951038949649,
    pt: 30927653813393,
    nl: 30995567423889,
    be: 32773744478609,
  },
  bulk_transfer_with_csv: {
    fr: 23947727003793,
    de: 23949260992145,
    it: 23949961425937,
    es: 23951017640209,
    pt: 30813746263569,
    nl: 30822751455889,
    be: 32795237455505,
  },
  bulk_transfer_with_xml: {
    fr: 23947703730961,
    de: 23949292168465,
    it: 23949961394065,
    es: 23951025739665,
    pt: 30830776448401,
    nl: 30829500284177,
    be: 32795400017169,
  },
  repayments_qonto_pay_later: {
    fr: 23947607384337,
    de: 23949199696017,
    it: 23949902225425,
    es: 23950903380241,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  what_apps_can_be_connected_to_qonto_account: {
    fr: 23947685520145,
    de: 23949255751185,
    it: 23949988349969,
    es: 23951013976209,
    pt: 30991689575313,
    nl: 31006293460241,
    be: 32570299043089,
  },
  what_is_2fa: {
    fr: 23947714708881,
    de: 23949284336273,
    it: 23949987615377,
    es: 23951000439441,
    pt: 31002795080977,
    nl: 31007385554193,
    be: 32458465488913,
  },
  checks_above15k: {
    fr: 23947667710353,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  fnci_how_it_works: {
    fr: 23947595117073,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  create_logo_with_qonto_ai: {
    fr: 23947598889617,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  rules_to_transfer_cash_contributions: {
    fr: 23947728089617,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  which_proof_of_incorportation_is_accepted: {
    fr: 23947579782545,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  difference_between_physical_person_and_legal_entity: {
    fr: 23947646383505,
    de: 23949178941329,
    it: undefined,
    es: 23950990391953,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  requirements_initial_capital_deposit: {
    fr: 23947712694033,
    de: 23949246749201,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_create_and_share_draft_invoices: {
    fr: 23947630802449,
    de: 23949229027857,
    it: 23949950685329,
    es: 23950935066001,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_create_self_billed_invoice: {
    fr: undefined,
    de: undefined,
    it: 23949893469329,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  company_registered_required_documents: {
    fr: 23947739978769,
    de: 23949286422801,
    it: 23950016673041,
    es: 23951016601745,
    pt: 30359527698065,
    nl: 30792062675217,
    be: undefined,
  },
  what_documents_you_need_to_update_information_in_profile: {
    fr: undefined,
    de: undefined,
    it: 23949918203793,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  what_is_sca: {
    fr: 23947670125329,
    de: 23949223040657,
    it: 23949944205201,
    es: 23950977291409,
    pt: 31031272354833,
    nl: 31138524546193,
    be: 32475999026833,
  },
  how_to_collect_money_from_my_clients: {
    fr: 25012107576209,
    de: 25302455344273,
    it: 25276624926225,
    es: 25193273020177,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  pay_later_valid_invoice: {
    fr: 25317181693201,
    de: 25895540400273,
    it: 25317351863569,
    es: 25317436448017,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_does_qonto_partner_with_wise: {
    fr: 26099611324561,
    de: 26103452161553,
    it: 26100478455953,
    es: 26099958537105,
    pt: 31103229844497,
    nl: 31673773578129,
    be: 32410483395601,
  },
  how_to_make_transfer_in_jpy: {
    fr: 26668002250001,
    de: 26746974986257,
    it: 26718692752017,
    es: 26718468270481,
    pt: 31038693469329,
    nl: 31144359049233,
    be: 32773296339089,
  },
  customize_approval_workflows_for_transfer_reimbursement_supplier_invoice: {
    fr: 26570070089745,
    de: 26057885522193,
    it: 26572788437137,
    es: 26624250395921,
    pt: 31102972980113,
    nl: 31671923527697,
    be: 32670897060113,
  },
  how_to_use_custom_dates: {
    fr: 24774970693521,
    de: 25045863185041,
    it: 24320116097553,
    es: 24234145227025,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_detect_fraud_attempts: {
    fr: 23947606808209,
    de: 23949190237713,
    it: 23949901844497,
    es: 23950903121937,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_setup_recurring_invoice: {
    fr: 24400330200081,
    de: 27078560818833,
    it: 24323019852561,
    es: 24234162723473,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  taxes_in_spain: {
    fr: undefined,
    de: undefined,
    it: undefined,
    es: 23950951245073,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  attachments_auto_matching: {
    fr: 23947627750929,
    de: 23949228399505,
    it: 23949938278929,
    es: 23950954720145,
    pt: 31106594424209,
    nl: 31675703683473,
    be: 32415278501905,
  },
  insurance_hub: {
    fr: 27761716992529,
    de: 27767518045585,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  insurance_hub_surein: {
    fr: undefined,
    de: 27767518045585,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  insurance_hub_facile: {
    fr: undefined,
    de: undefined,
    it: 31520132771729,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  insurance_hub_insify: {
    fr: undefined,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: 40923144757649,
    be: undefined,
  },
  de_einvoice: {
    fr: undefined,
    de: 30833026928785,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  veri_factu: {
    be: undefined,
    de: undefined,
    es: 35831015259281,
    fr: undefined,
    it: undefined,
    nl: undefined,
    pt: undefined,
  },
  what_is_e_invoicing: {
    fr: 27885538176913,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  what_company_creation_package_includes: {
    fr: undefined,
    de: undefined,
    it: undefined,
    es: 23950875213713,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_does_opening_gbr_at_qonto_work: {
    fr: undefined,
    de: 23949249860497,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  egbr_faq: {
    fr: undefined,
    de: 30577022062865,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  can_any_organization_open_qonto_account: {
    fr: 23947720386833,
    de: 23949292696849,
    it: 23950016696593,
    es: 23951055693329,
    pt: 30360472580497,
    nl: undefined,
    be: 32801191664273,
  },
  can_i_open_account_for_company_being_created: {
    fr: undefined,
    de: undefined,
    it: 23949893290897,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  company_has_legal_entity_as_shareholder_who_are_ubos: {
    fr: undefined,
    de: undefined,
    it: 23949947744913,
    es: undefined,
    pt: undefined,
    nl: 31382394489361,
    be: 32924913045521,
  },
  where_can_i_find_fiscal_code: {
    fr: undefined,
    de: undefined,
    it: 23949981469457,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_fill_existing_company_ultimate_beneficial_owner_during_registration_process: {
    fr: 23947632136721,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: 32796119733265,
  },
  activities_prohibited_at_qonto: {
    fr: 23947637582609,
    de: 23949232223121,
    it: 23949956723729,
    es: 23950976136721,
    pt: 30357958875409,
    nl: 30399386083985,
    be: 32797958086417,
  },
  fcc_sept24: {
    fr: 27221960857873,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  commercial_register_document_required_for_company_in_formation: {
    fr: undefined,
    de: 23949271340561,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  deposit_share_capital_of_my_new_company: {
    fr: undefined,
    de: undefined,
    it: 23949933116049,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_does_account_transfer_service_work: {
    fr: 23947677617809,
    de: undefined,
    it: undefined,
    es: 23950991413905,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_follow_transfer_progress: {
    fr: 23947652550417,
    de: undefined,
    it: undefined,
    es: 23950975026449,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  increase_sdd_collection_limit: {
    fr: 30259897998609,
    de: 30260466526609,
    it: 30260415007377,
    es: 30260289127185,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  forwarding_email_address_faq: {
    fr: 23947591709457,
    de: 23949199514385,
    it: 23949947229841,
    es: 23950919490833,
    pt: 31040371418001,
    nl: 31145476354065,
    be: 32554552802577,
  },
  allowed_countries_for_international_transfer: {
    fr: 32263876029713,
    de: 32263510028305,
    it: 32263982649745,
    es: 32263921884433,
    pt: 32264067304465,
    nl: undefined,
    be: undefined,
  },
  quotes_electronic_signature: {
    de: 33781209086865,
    es: 33772880570257,
    fr: 33770576938001,
    it: 33776755446033,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  qonto_facturacion_get_started: {
    fr: 35697219381009,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  finanzamt_tax_application: {
    fr: undefined,
    de: 36259482117649,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  terminal_delivery_address_zipcode_error: {
    fr: 35438211302417,
    de: 35436506307985,
    it: 35438211302417,
    es: 35451548628753,
    pt: 35438211302417,
    nl: 35438211302417,
    be: 35438211302417,
  },
  accounts_remuneration_get_started: {
    fr: 37706948525841,
    de: undefined,
    it: undefined,
    es: 38018222744849,
    pt: undefined,
    nl: 37707885061009,
    be: undefined,
  },
  accounts_remuneration_requirements: {
    fr: 37708918203921,
    de: undefined,
    it: undefined,
    es: 37709028984977,
    pt: undefined,
    nl: 37709123358353,
    be: undefined,
  },
  capital_increase_eligibility: {
    fr: 23947673132177,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  capital_increase_documents: {
    fr: 38720641656977,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_manage_payment_link_methods: {
    fr: 38957958697489,
    de: 38982223936785,
    it: 38957958697489,
    es: 38957958697489,
    pt: 38957958697489,
    nl: 39053895390609,
    be: 38957958697489,
  },
  how_check_via_kvk_open_account: {
    fr: undefined,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: 39071199278993,
    be: undefined,
  },
  payment_links: {
    fr: 32614584737425,
    de: 32613895529745,
    it: 32614584737425,
    es: 32614584737425,
    pt: 32614584737425,
    nl: 32615132296337,
    be: 32614584737425,
  },
  verification_of_payee: {
    fr: 39107454970769,
    de: 39130581174545,
    it: 39274024823953,
    es: 39125099259537,
    pt: 39107454970769,
    nl: 39278695354769,
    be: 39107454970769,
  },
  de_account_opening_requirements: {
    fr: undefined,
    de: 23949210523153,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_to_identify_the_ultimate_beneficial_owners_of_your_company: {
    fr: undefined,
    de: undefined,
    it: 31002633103633,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  cfm_google_sheets_how_sync_works: {
    fr: 40644235297809,
    de: 40718935656593,
    it: 40436206119953,
    es: 40674558441617,
    pt: undefined,
    nl: 40720405191441,
    be: undefined,
  },
  investments: {
    fr: undefined,
    de: 40706278053137,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  what_is_credit_card: {
    fr: 39616169655825,
    de: 39612515092369,
    it: 39563632414481,
    es: 39536184604049,
    pt: 39616169655825,
    nl: 39616169655825,
    be: undefined,
  },
  cards_legal_advice: {
    fr: 23947639722897,
    de: 23949233660689,
    it: 23949963006609,
    es: 23950925916689,
    pt: 29853123638929,
    nl: 37431871761297,
    be: 32679250521873,
  },
  capital_deposit_bylaws: {
    fr: 23947696291345,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
  how_do_i_earn_cashback_with_my_qonto_card: {
    fr: 44818631418641,
    de: 44818830649873,
    it: 44818941670289,
    es: 44819050079377,
    pt: 44818622279313,
    nl: 44819011169425,
    be: undefined,
  },
  designate_qonto_as_pa: {
    fr: 44861798578193,
    de: undefined,
    it: undefined,
    es: undefined,
    pt: undefined,
    nl: undefined,
    be: undefined,
  },
};
